import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useApi from "../../hooks/useApi";
import Select from "react-select";
import DatePicker from "react-datepicker";
import moment from "moment";
import Swal from "sweetalert2";
import { activeStatus } from "../../widgets/PolicyItemStatus";
import useReload from "../../hooks/useReload";
import useApp from "../../hooks/useApp";
import { Container, Form, InputGroup, Row, Col } from "react-bootstrap";

const PolicyUnderwriteForm = (props) => {
  const { itemid = null } = useParams();
  const titleRef = useRef();
  const branchRef = useRef();
  const policyNumberRef = useRef();
  const premiumRef = useRef();

  const [policyCurrency, setPolicyCurrency] = useState("GHS");

  const [policy, setPolicy] = useState({});
  const [underwritePolicy, setUnderwritePolicy] = useState({});
  const [dropDowns, setDropDowns] = useState({ branch: [] });
  const { api, abortSignal, auth } = useApi();
  const { showAlert } = useApp();
  const { toggleReload, reload } = useReload();
  const [startDate, setStartDate] = useState(new Date());

  const getDropDowns = async (models) => {
    try {
      const { data } = await api.list(`/dropdowns`, models);
      return data;
    } catch (error) {
      console.error(error);
    }
  };

  const companyDropDown = async () => {
    try {
      const data = await getDropDowns(["company"]);
      if (data && typeof policy.CompanyId === "string")
        setPolicy({
          ...policy,
          CompanyId: data.company.find(
            (item) => item.value == policy.CompanyId
          ),
        });
      setDropDowns({ ...dropDowns, ...data });
    } catch (error) {}
  };

  const branchDropDown = async () => {
    try {
      const data = await getDropDowns([
        {
          branch: { CompanyId: policy.CompanyId.value },
        },
      ]);
      if (data && typeof policy.BranchId === "string")
        setPolicy({
          ...policy,
          BranchId: data.branch.find((item) => item.value == policy.BranchId),
        });
      setDropDowns({ ...dropDowns, ...data });
    } catch (error) {}
  };

  const getPolicy = async () => {
    try {
      if (itemid) {
        const { data = {} } = await api.read(
          `/policies/policy-items/${itemid}`
        );

        setPolicy({
          ...data,
          effectiveDate: data?.effectiveDate
            ? new Date(data?.effectiveDate)
            : new Date(),
          policyCurrency: {
            label: data.policyCurrency,
            value: data.policyCurrency,
          },
        });
      }
    } catch (error) {}
  };

  useEffect(() => {
    getPolicy();
  }, [auth, itemid, reload]);

  useEffect(() => {
    companyDropDown();
  }, [policy]);

  useEffect(() => {
    branchDropDown();
  }, [policy.CompanyId]);

  const handleUnderwrite = async (e) => {
    e.preventDefault();
    if (!itemid) {
      return showAlert("Could not underwrite policy, missing policy", "error");
    }
    try {
      const data = {
        ...policy,
        duration: policy?.duration || 12,
        BranchId: policy?.BranchId?.value || null,
        CompanyId: policy?.CompanyId?.value || null,
        policyCurrency: policy.policyCurrency?.value || "",
        deferCommission: policy?.deferCommission || false,
      };

      delete data?.Company;
      delete data.Product;
      delete data.PolicyType;
      delete data.id;
      const response = await api.update(
        `/policies/policy-items/${itemid}/underwrite`,
        data
      );
      if (response.ok) {
        showAlert("Policy underwritten pending activation", "info");
        toggleReload();
      }
    } catch (error) {
      // console.error(error);
    }
  };

  const handlePolicyActivation = async () => {
    try {
      const data = {
        ...policy,
        CompanyId: policy?.CompanyId?.value || null,
        BranchId: policy?.BranchId?.value || null,
        policyCurrency: policy.policyCurrency?.value || "",
        deferCommission: policy?.deferCommission || false,
      };

      if (!data.BranchId) {
        branchRef.current.focus();
        showAlert("Please select underwriting branch", "error");
        return;
      }

      if (!data.title) {
        titleRef.current.focus();
        showAlert("Please enter appropriate title for this policy", "error");
        return;
      }

      if (!Number(data.premium) > 0) {
        premiumRef.current.focus();
        showAlert("Premiun is invalid", "error");
        return;
      }

      if (!data.policyNumber) {
        policyNumberRef.current.focus();
        showAlert("Please enter policy number", "error");
        return;
      }

      delete data.Company;
      delete data.Product;
      delete data.PolicyType;
      delete data.id;

      const response = await api.update(
        `/policies/policy-items/${itemid}/activate`,
        data
      );
      if (response.ok) {
        showAlert("Policy activated", "success");
        toggleReload();
        // setShowUnderwrite(false);
      } else {
        showAlert(response.message, "error");
      }
    } catch (error) {}
  };

  const handleRevoke = async () => {
    const callRevoke = async () => {
      try {
        if (itemid) {
          const response = await api.update(
            `/policies/policy-items/${itemid}/revoke`
          );
          if (response.ok) {
            Swal.fire("Revoked!", `Policy revoked`, "success");
            toggleReload();
            // setShowUnderwrite(false);
          } else {
            Swal.fire("Revoke failed!", `Policy not provided`, "info");
          }
        } else {
          Swal.fire("Revoke failed!", `Policy not provided`, "error");
        }
      } catch (error) {
        //console.error(error);
        Swal.fire(
          "Error",
          `Failed to remove policy - ${error.message}`,
          "error"
        );
      }
    };
    Swal.fire({
      title: "Revoke Policy Item?",
      text: "Do you want to continue",
      icon: "warning",
      allowOutsideClick: false,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        callRevoke();
      }
    });
  };

  const handleRevokeRequest = async (id) => {
    const { value: comment, isConfirmed } = await Swal.fire({
      title: "Are you sure?",
      text: "You want revoke this policy!",
      icon: "warning",
      input: "textarea",
      inputLabel: "",
      inputPlaceholder: "Type your reason here...",
      inputAttributes: {
        "aria-label": "Type your Reason here",
        required: true,
      },
      inputValidator: (value) => {
        if (!value) {
          return "You need to enter reason";
        }
      },
      confirmButtonText: "Yes, Revoke",
      showCancelButton: true,
    });

    if (isConfirmed) {
      try {
        const data = { reason: comment || "", PolicyItemId: id };
        const response = await api.add(
          `/policies/policy-items/request-revoke`,
          data
        );
        if (response.ok) {
          showAlert("Policy revoke submitted", "success");
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    const currency = policy.policyCurrency?.value || "";
    setPolicyCurrency(currency);
  }, [policy.policyCurrency]);

  useEffect(() => {
    setPolicy({
      ...policy,
      expires: moment(policy?.effectiveDate || new Date())
        .add(policy?.duration || 12, "months")
        .subtract(1, "day")
        .format("YYYY-MM-DD"),
    });
  }, [policy?.duration, policy?.effectiveDate]);

  return (
    <Form
      onSubmit={handleUnderwrite}
      autoComplete="off"
      id="underwriteForm"
      className="mb-3"
    >
      {policy && (
        <div className="card h-100">
          <fieldset
            disabled={activeStatus.includes(policy?.status) ? true : false}
          >
            <div className="card-body">
              <Row>
                <Container fluid="sm">
                  <Row className="mb-3">
                    <Form.Group>
                      <Form.Label className="text-primary">Title</Form.Label>
                      <Form.Control
                        id="title"
                        type="text"
                        ref={titleRef}
                        onChange={(e) =>
                          setPolicy({
                            ...policy,
                            title: e.target.value,
                          })
                        }
                        value={policy?.title || ""}
                      />
                      <Form.Text>This is label for the policy</Form.Text>
                    </Form.Group>
                  </Row>
                  <Row className="mb-4">
                    <Col sm={12} md={6}>
                      <Form.Group>
                        <Form.Label className="text-primary">
                          Product
                        </Form.Label>
                        <Form.Control
                          plaintext
                          readOnly
                          value={`${policy?.PolicyType?.name} - ${policy?.Product?.name}`}
                          onChange={() => {}}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={12} md={6}>
                      <Form.Group>
                        <Form.Label className="text-primary">
                          Insuring Company
                        </Form.Label>
                        <Select
                          id="company"
                          options={
                            dropDowns && dropDowns["company"]
                              ? dropDowns["company"]
                              : []
                          }
                          onChange={(selectedOption) =>
                            setPolicy({
                              ...policy,
                              CompanyId: selectedOption,
                              BranchId: "",
                            })
                          }
                          value={policy?.CompanyId || ""}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-4">
                    <Col sm={12} md={6}>
                      <Form.Group>
                        <Form.Label className="text-primary">
                          Underwriting Branch
                        </Form.Label>
                        <Select
                          ref={branchRef}
                          id="branch"
                          options={
                            dropDowns && dropDowns["branch"]
                              ? dropDowns["branch"]
                              : []
                          }
                          onChange={(selectedOption) =>
                            setPolicy({ ...policy, BranchId: selectedOption })
                          }
                          value={policy?.BranchId || ""}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={12} md={6}>
                      <Form.Group>
                        <Row>
                          <Col>
                            <Form.Label className="text-primary">
                              Policy Number
                            </Form.Label>
                          </Col>
                          {policy?.Policy?.policyNumber && (
                            <Col>
                              <div className="form-check form-switch">
                                <Form.Label className="text-primary">
                                  Use Parent Policy Number
                                </Form.Label>

                                <Form.Control
                                  id="copy-policy-number"
                                  className="form-check-input me-3"
                                  type="checkbox"
                                  checked={
                                    policy?.policyNumber ==
                                    policy?.Policy?.policyNumber
                                      ? true
                                      : false
                                  }
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setPolicy({
                                        ...policy,
                                        policyNumber:
                                          policy?.Policy?.policyNumber,
                                      });
                                    }
                                  }}
                                />
                              </div>
                            </Col>
                          )}
                        </Row>

                        <Form.Control
                          ref={policyNumberRef}
                          id="policy-number"
                          type="text"
                          onChange={(e) =>
                            setPolicy({
                              ...policy,
                              policyNumber: e.target.value,
                            })
                          }
                          value={policy?.policyNumber || ""}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col sm={12} md={6}>
                      <Form.Group>
                        <Form.Label className="text-primary">
                          Policy Currency
                        </Form.Label>
                        <Select
                          id="currency"
                          options={[
                            { label: "GHS", value: "GHS" },
                            { label: "USD", value: "USD" },
                          ]}
                          onChange={(selectedOption) =>
                            setPolicy({
                              ...policy,
                              policyCurrency: selectedOption,
                            })
                          }
                          value={policy?.policyCurrency || "GHS"}
                        />
                      </Form.Group>
                    </Col>

                    <Col sm={12} md={6}>
                      <Form.Group>
                        <Form.Label className="text-primary">
                          Premium
                        </Form.Label>
                        <InputGroup className="mb-3">
                          <InputGroup.Text>
                            {policyCurrency || "GHS"}
                          </InputGroup.Text>
                          <Form.Control
                            aria-label="Amount (to the nearest value)"
                            ref={premiumRef}
                            id="premium"
                            type="number"
                            min={0}
                            step={"0.01"}
                            value={policy?.premium || 0.0}
                            onChange={(e) =>
                              setPolicy({
                                ...policy,
                                premium: e.target.value,
                              })
                            }
                          />
                          <InputGroup.Text className="fw-bold">
                            {Number.parseFloat(policy?.premium).toLocaleString(
                              "en-US"
                            )}
                          </InputGroup.Text>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col sm={12} md={6}>
                      <Form.Group>
                        <Form.Label className="text-primary">
                          Effective Date
                        </Form.Label>
                        <DatePicker
                          dateFormat="dd/MM/yyyy"
                          showMonthDropdown
                          showPopperArrow={false}
                          id="effective-date"
                          className="form-control"
                          selected={policy?.effectiveDate || startDate}
                          onChange={(date) =>
                            setPolicy({ ...policy, effectiveDate: date })
                          }
                          isClearable
                          placeholderText=""
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={12} md={6}>
                      <Form.Group>
                        <Form.Label className="text-primary">
                          Duration (in months)
                        </Form.Label>
                        <Form.Control
                          id="duration"
                          type="number"
                          required
                          placeholder="Duraton in months"
                          onChange={(e) =>
                            setPolicy({ ...policy, duration: e.target.value })
                          }
                          value={policy?.duration || 12}
                          min={1}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col sm={12} md={6}>
                      <Form.Group>
                        <Form.Label className="text-primary">
                          Created
                        </Form.Label>
                        <Form.Control
                          plaintext
                          onChange={(e) => {}}
                          value={
                            moment(policy?.createdAt).format("DD/MM/YYYY") || ""
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={12} md={6}>
                      <Form.Group>
                        <Form.Label className="text-primary">
                          Expires
                        </Form.Label>
                        <Form.Control
                          className="fs-4"
                          plaintext
                          id="expires"
                          onChange={(e) => {}}
                          value={
                            moment(policy?.expires).format("DD/MM/YYYY") || ""
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col sm={12} md={6}>
                      {(["waiting", "renewal"].includes(policy?.status) ||
                        policy.deferCommission == true) && (
                        <Form.Group>
                          <Form.Label className="text-primary">
                            Defer Commission
                          </Form.Label>
                          <div className="form-check form-switch">
                            <Form.Control
                              id="defer"
                              className="form-check-input mt-2"
                              type="checkbox"
                              aria-describedby="deferCommissionHelp"
                              checked={
                                policy.deferCommission == true ? true : false
                              }
                              onChange={(e) =>
                                setPolicy({
                                  ...policy,
                                  deferCommission: e.target.checked,
                                })
                              }
                            />
                            <br />
                            <br />
                            <Form.Text>
                              Please note this requires approval from Management
                              before policy is activated
                            </Form.Text>
                          </div>
                        </Form.Group>
                      )}
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Form.Group>
                      <Form.Label className="text-primary">
                        Other info
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        placeholder="Info"
                        onChange={(e) =>
                          setPolicy({
                            ...policy,
                            info: e.target.value,
                          })
                        }
                        value={policy?.info || ""}
                        rows="6"
                      />
                      <Form.Text>Enter details of the Policy</Form.Text>
                    </Form.Group>
                  </Row>
                </Container>
              </Row>
            </div>
          </fieldset>
          <div className="card-footer">
            <div className="d-flex justify-content-between">
              <button
                className={
                  activeStatus.includes(policy.status)
                    ? "btn btn-primary disabled"
                    : "btn btn-primary"
                }
                type="submit"
              >
                Save
              </button>
              {["waiting", "renewal"].includes(policy?.status) && (
                <button
                  className="btn btn-success"
                  type="button"
                  onClick={handlePolicyActivation}
                >
                  Activate
                </button>
              )}

              {["active", "inactive", "expiring"].includes(policy?.status) && (
                <button
                  className="btn btn-danger"
                  type="button"
                  onClick={
                    policy.canUnderwrite
                      ? handleRevoke
                      : () => {
                          handleRevokeRequest(itemid);
                        }
                  }
                >
                  Revoke
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Form>
  );
};

export default PolicyUnderwriteForm;
